/**
 * Live unit ⇄ floor sync: reads the store's units through the existing Unit
 * list query (which the unit socket invalidators already refresh), diffs
 * status/name against the drawn objects, and patches ONLY the stale ones —
 * one `patchObjects` batch → one static-picture re-record. This is the
 * "positions rarely change, metadata changes often" hot path.
 *
 * No new socket wiring: `.store.unit.*` events invalidate the unit list
 * cache via the existing cross-domain invalidators, React Query refetches,
 * and this effect folds the fresh rows onto the canvas.
 *
 * @module containers/Store/FloorPlan/hooks/useUnitStatusSync
 * @category Hooks
 */

import { useEffect } from 'react';

import useLazyGetUnitListApi from '@containers/Store/Unit/hooks/useLazyGetListApi';
import { buildUnitStatusPatches } from '@helpers/floorplan/unit-object';

import type { StoreUnit } from '@src/types/store/unit';

import { useFloorEngineStore } from '@src/floorplan/stores/useFloorEngineStore';

/**
 * @param planReady - Whether the plan is loaded and this surface owns the engine.
 * @param givenUnits - Units the caller already holds (the register's POS unit
 *   list). When given, the `/units` list is not read: it needs a unit
 *   permission that a cashier may not have, which froze the colours for them
 *   (list-needs-area-permission-staff-without).
 */
const useUnitStatusSync = (
  planReady: boolean,
  givenUnits?: StoreUnit[] | undefined,
): { units: StoreUnit[] } => {
  const { data } = useLazyGetUnitListApi(undefined, {
    enabled: planReady && givenUnits === undefined,
  });
  const units = givenUnits ?? data?.data;

  useEffect(() => {
    if (!planReady || units === undefined) return;
    const state = useFloorEngineStore.getState();
    const patches = buildUnitStatusPatches(
      units,
      Object.values(state.objectsById),
    );
    if (patches.length > 0) state.patchObjects(patches);
  }, [planReady, units]);

  return { units: units ?? [] };
};

export default useUnitStatusSync;
